import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { verificationAPI } from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const PhotoVerification = () => {
  const [photos, setPhotos] = useState({ profilePhotos: [], galleryPhotos: [] });
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const response = await verificationAPI.getPendingPhotos();
      setPhotos(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch photos');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (type, id) => {
    try {
      await verificationAPI.approvePhoto(type, id);
      toast.success('Photo approved successfully');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to approve photo');
    }
  };

  const handleReject = async (type, id) => {
    try {
      await verificationAPI.rejectPhoto(type, id);
      toast.success('Photo rejected and removed');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to reject photo');
    }
  };

  const PhotoCard = ({ photo, type, title }) => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {photo.first_name} {photo.last_name}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          User ID: {photo.user_id} | Phone: {photo.phone}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {title}
        </Typography>

        <Box sx={{ mt: 2, position: 'relative' }}>
          <img 
            src={type === 'profile' ? photo.profile_photo_url : photo.image_url}
            alt="Pending"
            style={{ 
              width: '100%', 
              height: 200, 
              objectFit: 'cover', 
              borderRadius: 8,
              cursor: 'pointer'
            }}
            onClick={() => {
              setPreviewUrl(type === 'profile' ? photo.profile_photo_url : photo.image_url);
              setPreviewOpen(true);
            }}
          />
        </Box>

        <Box display="flex" gap={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<ApproveIcon />}
            fullWidth
            onClick={() => handleApprove(type, photo.id)}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<RejectIcon />}
            fullWidth
            onClick={() => handleReject(type, photo.id)}
          >
            Reject
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Photo Verification
        </Typography>
        <Button variant="contained" onClick={fetchPhotos}>
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Photos */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Pending Profile Photos ({photos.profilePhotos?.length || 0})
          </Typography>
          <Grid container spacing={3}>
            {photos.profilePhotos?.map((photo) => (
              <Grid item xs={12} md={6} lg={4} key={`profile-${photo.id}`}>
                <PhotoCard photo={photo} type="profile" title="Profile Photo" />
              </Grid>
            ))}
            {photos.profilePhotos?.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">No pending profile photos</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Gallery Photos */}
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            Pending Gallery Photos ({photos.galleryPhotos?.length || 0})
          </Typography>
          <Grid container spacing={3}>
            {photos.galleryPhotos?.map((photo) => (
              <Grid item xs={12} md={6} lg={4} key={`gallery-${photo.id}`}>
                <PhotoCard photo={photo} type="gallery" title="Gallery Photo" />
              </Grid>
            ))}
            {photos.galleryPhotos?.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">No pending gallery photos</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <img src={previewUrl} alt="Preview" style={{ width: '100%' }} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PhotoVerification;